import { FC } from "react";
import { Paper, Skeleton } from "@mui/material";
import GraphMap from "../../../../../../../components/charts/GraphMap";
import TripLayer from "../../../../../../../components/charts/GraphMap/layers/TripLayer";
import Legend from "../../../../../../../components/charts/GraphMap/legends/Legend";
import { calcMapConfig, tripNodeIcon } from "../../../../../../../components/charts/GraphMap/utils";
import { TripArcDTO } from "../../../../../../../datasources/api-client/data-contracts";
import { useChartColors } from "../../../../../../../theme/theme";
import { ThemeColors } from "../../../../../../../theme/themeValues";

interface Props {
	isLoading: boolean;
	arcs?: TripArcDTO[];
	height?: string | number;
}

/**
 * Renders the Map of the trip, showing both arcs and the associated nodes
 * @param param0
 * @returns
 */
const TripMap: FC<Props> = ({ isLoading, arcs, height = "100%" }) => {
	const { chartColors } = useChartColors();

	if (isLoading) return <Skeleton variant="rounded" height={height} width="100%" />;

	// defining transport type colors (each color is at index "transportTypeId" - 1)
	const transportTypeColors = [
		chartColors.roadType,
		chartColors.trainType,
		chartColors.shipType,
		chartColors.airType,
	];

	// get all transport types from the arcs
	const transportTypes = arcs?.map((arc) => {
		return {
			id: arc.transportTypeId,
			label: arc.transportTypeDescription,
			color: transportTypeColors[arc.transportTypeId - 1],
		};
	});
	// removing duplicates by arc transportTypeId
	let temp = new Set();
	const legendColors = transportTypes?.filter(({ id }) => !temp.has(id) && temp.add(id));

	// get all node types (among with the associated icon and position) from the arcs
	const nodes =
		arcs && arcs.length > 0
			? [
					{
						id: arcs[0].origin.typeId,
						label: arcs[0].origin.typeDescription,
						icon: tripNodeIcon(arcs[0].origin.typeId, ThemeColors.LightAction),
						lat: arcs[0].origin.lat,
						lng: arcs[0].origin.lng,
					},
				]
			: [];

	arcs?.map((arc) =>
		nodes.push({
			id: arc.destination.typeId,
			label: arc.destination.typeDescription,
			icon: tripNodeIcon(arc.destination.typeId, ThemeColors.LightAction),
			lat: arc.destination.lat,
			lng: arc.destination.lng,
		})
	);
	// removing duplicates by node typeId
	temp = new Set();
	const legendIcons = nodes.filter(({ id }) => !temp.has(id) && temp.add(id));

	const mapConfig = calcMapConfig(nodes);

	// // *TEMP* used to show every used icons in the legend (testing and styling purposes)
	// const legendIcons = [
	// 	{ label: "Magazzino di stabilimento", icon: tripNodeIcon(1, ThemeColors.LightAction, 16) },
	// 	{ label: "Magazzino centrale", icon: tripNodeIcon(2, ThemeColors.LightAction, 16) },
	// 	{ label: "Magazzino periferico", icon: tripNodeIcon(3, ThemeColors.LightAction, 16) },
	// 	{ label: "Transit point", icon: tripNodeIcon(4, ThemeColors.LightAction, 16) },
	// 	{ label: "Interporto/Nodo ferroviario", icon: tripNodeIcon(5, ThemeColors.LightAction, 16) },
	// 	{ label: "Porto marittimo", icon: tripNodeIcon(6, ThemeColors.LightAction, 16) },
	// 	{ label: "Aeroporto", icon: tripNodeIcon(7, ThemeColors.LightAction, 16) },
	// 	{ label: "Ce.Di. GDO/Retail", icon: tripNodeIcon(8, ThemeColors.LightAction, 16) },
	// 	{ label: "Cliente", icon: tripNodeIcon(9, ThemeColors.LightAction, 16) },
	// 	{ label: "Stabilimento", icon: tripNodeIcon(10, ThemeColors.LightAction, 16) },
	// 	{ label: "Ufficio", icon: tripNodeIcon(11, ThemeColors.LightAction, 16) },
	// 	{ label: "Commercial Warehouse", icon: tripNodeIcon(12, ThemeColors.LightAction, 16) },
	// 	{ label: "Industrial Warehouse", icon: tripNodeIcon(13, ThemeColors.LightAction, 16) },
	// 	{ label: "Industrial Copacker", icon: tripNodeIcon(14, ThemeColors.LightAction, 16) },
	// 	{ label: "Raw material / Packaging", icon: tripNodeIcon(15, ThemeColors.LightAction, 16) },
	// ];

	return (
		<Paper sx={{ padding: 2, position: "relative", height: height }} elevation={3}>
			<GraphMap
				height={"100%"}
				isLoading={isLoading}
				tileProvider="heremaps"
				minZoom={mapConfig.zoom}
				maxZoom={17}
				zoom={mapConfig.zoom}
				center={mapConfig.center}
			>
				<TripLayer arcs={arcs} />
			</GraphMap>
			<Legend colors={legendColors} icons={legendIcons} maxWidth={"25%"} maxHeight={"300px"} />
		</Paper>
	);
};

export default TripMap;
